/**
 * S3 · The match Stats tab: what this match produced, and what it feeds.
 *
 * Two questions, in order. What numbers came out of this match? And which
 * totals above it move when it changes? The lines come first because they are
 * the thing that exists — everything above is made from them.
 */

import { useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  Card,
  Empty,
  LinkButton,
  PageHead,
  Pill,
  Problem,
  Section,
  Stat,
  StatGrid,
  Table,
  Td,
  Th,
  Toolbar,
  Stack,
} from "@/components/kit";
import { ago, loadFixture, type FixtureView } from "@/lib/statsEngine/api";
import { useApi } from "@/spa/useApi";

import { Dot } from "./StatsTabPage";

/** A line's numbers, as `runs 54 · balls 38`. One row per subject per part of
 *  the match, with every number they made together. */
function numbers(values: unknown): string {
  return Object.entries((values ?? {}) as Record<string, unknown>)
    .map(([k, v]) => `${k} ${String(v)}`)
    .join(" · ");
}

export function FixtureStatsPage() {
  const { sport = "", fixtureId = "" } = useParams<{ sport: string; fixtureId: string }>();
  const load = useCallback(() => loadFixture(fixtureId), [fixtureId]);
  const { data, loading, error } = useApi<FixtureView>(
    load,
    [fixtureId],
    "the admin API is not reachable",
  );

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;
  if (!data) return <PageHead title="Match stats" note={loading ? "Reading…" : ""} />;

  const subjects = new Set(data.lines.map((l) => String(l.subject_name ?? ""))).size;

  return (
    <Stack>
      <PageHead
        title={`Stats · ${data.name}`}
        note="What this match produced, and what it feeds. Every line is one subject in one part of the match; the totals above are made from these."
        actions={
          <Toolbar>
            <LinkButton to={`/${sport}/fixtures/${fixtureId}`}>Fixture</LinkButton>
            <LinkButton to={`/${sport}/stats-engine`}>Stats</LinkButton>
          </Toolbar>
        }
      />

      <StatGrid>
        <Stat label="Status" value={<Pill tone={data.status === "COMPLETED" ? "good" : "live"} dot>{data.status}</Pill>} />
        <Stat label="Balls" value={data.lastSeq} note="what the ledger holds" />
        <Stat label="Lines" value={data.lines.length} note={`${subjects} subjects`} />
        <Stat label="Feeds" value={data.feeds.length} note="totals above that read this match" />
      </StatGrid>

      <Section
        title="Lines"
        note="One row per subject per part of the match, with all of their numbers together."
      >
        {data.lines.length === 0 ? (
          <Card>
            <Empty
              title="No lines yet"
              note="The sport's own action writes these inside the command that scores a ball. Score one and they appear."
            />
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Who</Th>
                <Th>Role</Th>
                <Th>Part</Th>
                <Th>Numbers</Th>
                <Th num>Place</Th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((line, i) => (
                <tr key={i}>
                  <Td strong>{String(line.subject_name ?? "")}</Td>
                  <Td quiet>{String(line.subject_role ?? "") || "entrant"}</Td>
                  <Td quiet>{String(line.segment ?? "") || "whole match"}</Td>
                  <Td>{numbers(line.values)}</Td>
                  <Td num>
                    {line.rank === null || line.rank === undefined ? "—" : String(line.rank)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section title="What it feeds" note="The totals and tables above this match that read its numbers.">
        {data.feeds.length === 0 ? (
          <Card>
            <Empty
              title="Nothing reads it yet"
              note="A stat that counts over a season or a career will appear here once one is attached."
            />
          </Card>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Stat</Th>
                <Th>Where</Th>
                <Th>Counts over</Th>
                <Th>Lane</Th>
              </tr>
            </thead>
            <tbody>
              {data.feeds.map((feed) => (
                <tr key={`${feed.key}:${feed.scope_ref}`}>
                  <Td strong>{feed.key}</Td>
                  {/* By name, never a truncated id: two ancestors of one match
                      are two different nodes, and cut ids make them look like
                      the same row twice. */}
                  <Td>{feed.where}</Td>
                  <Td quiet>{feed.scope_kind}</Td>
                  <Td quiet>{feed.lane}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      {data.stats.length > 0 ? (
        <Section title="Stats on this match" note="Anything computed at match scope.">
          <Table>
            <thead>
              <tr>
                <Th>Stat</Th>
                <Th>Numbers</Th>
                <Th>Last run</Th>
              </tr>
            </thead>
            <tbody>
              {data.stats.map((line) => (
                <tr key={line.key}>
                  <Td strong>{line.key}</Td>
                  <Td>
                    <Dot status={line.status} />
                  </Td>
                  <Td quiet>{ago(line.lastRunAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      ) : null}
    </Stack>
  );
}
